import React from 'react';
import { formatDateLocaleLima } from '../../utils/dateUtils';

const pendingLinesCount = (req) =>
  (req.items || []).filter((ri) => parseFloat(ri.pending_quantity ?? ri.quantity ?? 0) > 0.0001).length;

/**
 * Lista de requerimientos para modal de referencias (documentos / guías).
 * mode: multi = checkboxes; single = clic en fila.
 */
const RequirementReferencePicker = ({
  requirements = [],
  mode = 'multi',
  selectedIds = [],
  onToggle,
  onSelectSingle,
  loading = false,
}) => {
  if (loading) {
    return <p className="text-sm text-gray-500 py-2">Cargando requerimientos...</p>;
  }

  if (!requirements.length) {
    return <p className="text-sm text-gray-500">No hay requerimientos con ítems pendientes</p>;
  }

  const selectedSet = new Set((selectedIds || []).map(String));

  return (
    <div className="space-y-2 max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-3">
      {requirements.map((req) => {
        const sid = String(req.id);
        const isSelected = selectedSet.has(sid);
        const pct = req.fulfillment_percent ?? 0;
        const lines = pendingLinesCount(req);
        const disabled = lines === 0;

        if (mode === 'single') {
          return (
            <div
              key={req.id}
              role="button"
              tabIndex={disabled ? -1 : 0}
              onClick={() => !disabled && onSelectSingle?.(req.id)}
              onKeyDown={(e) => {
                if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  onSelectSingle?.(req.id);
                }
              }}
              className={`p-3 border rounded-lg transition-colors ${
                disabled
                  ? 'opacity-50 cursor-not-allowed bg-gray-50 border-gray-200'
                  : 'cursor-pointer border-gray-200 hover:border-teal-500 hover:bg-teal-50'
              }`}
            >
              <RequirementRowContent req={req} pct={pct} lines={lines} />
              {!disabled && (
                <button type="button" className="mt-2 px-3 py-1 bg-teal-600 text-white rounded-md text-sm hover:bg-teal-700">
                  Seleccionar
                </button>
              )}
            </div>
          );
        }

        return (
          <label
            key={req.id}
            className={`flex items-start gap-3 p-3 border rounded-lg transition-colors ${
              disabled
                ? 'opacity-50 cursor-not-allowed bg-gray-50 border-gray-200'
                : isSelected
                ? 'bg-teal-50 border-teal-300 cursor-pointer'
                : 'bg-white border-gray-200 hover:bg-gray-50 cursor-pointer'
            }`}
          >
            <input
              type="checkbox"
              checked={isSelected}
              disabled={disabled}
              onChange={() => !disabled && onToggle?.(req.id, !isSelected)}
              className="mt-1 w-4 h-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
            />
            <div className="flex-1 min-w-0">
              <RequirementRowContent req={req} pct={pct} lines={lines} />
            </div>
          </label>
        );
      })}
    </div>
  );
};

const RequirementRowContent = ({ req, pct, lines }) => (
  <>
    <div className="flex flex-wrap items-center justify-between gap-2">
      <span className="font-mono font-medium text-teal-800">{req.code}</span>
      <span className="text-xs text-gray-500">{formatDateLocaleLima(req.request_date)}</span>
    </div>
    <div className="text-xs text-gray-600 mt-1">
      {lines} línea(s) pendiente(s) · {pct}% atendido
      {req.needed_by_date ? ` · Req. ${formatDateLocaleLima(req.needed_by_date)}` : ''}
    </div>
    <div className="mt-2 flex items-center gap-2 max-w-xs">
      <div className="flex-1 bg-gray-200 rounded-full h-1.5">
        <div className="bg-teal-600 h-1.5 rounded-full" style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      <span className="text-xs text-gray-500">{pct}%</span>
    </div>
  </>
);

export default RequirementReferencePicker;
